import React, { useRef, useEffect, useState, useMemo } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import '../styles/button.css';
import { getQuizById } from '../data/quizRegistry';
import { getResultTier } from '../utils/quizUtils';
import { getPercentile } from '../utils/percentile';
import { generateResultImage, downloadImage } from '../utils/generateResultImage';
import { saveResult, ResultRecord } from '../utils/resultHistory';
import { updateMetaTags } from '../utils/seoHelpers';
import ResultCard from '../components/ResultCard';
import ShareButtons from '../components/ShareButtons';
import HealthTipsComponent from '../components/HealthTips';
import ScoreBreakdown from '../components/ScoreBreakdown';
import ResultHistory from '../components/ResultHistory';
import ChallengeModal from '../components/ChallengeModal';
import QuizRecommendations from '../components/QuizRecommendations';
import AdUnit from '../ads/AdUnit';

const ResultPage: React.FC = () => {
  const { quizId } = useParams<{ quizId: string }>();
  const location = useLocation();
  const navigate = useNavigate();
  const score: number = location.state?.score || 0;
  const answers: boolean[] = location.state?.answers || [];
  const resultCardRef = useRef<HTMLDivElement>(null);
  const [saving, setSaving] = useState(false);

  const quiz = useMemo(() => (quizId ? getQuizById(quizId) : undefined), [quizId]);

  const value = quiz ? quiz.baseValue + score : 0;
  const percentile = quiz ? getPercentile(score, quiz.percentile.mean, quiz.percentile.sd) : 0;
  const tier = quiz ? getResultTier(score, quiz) : null;
  const resultTitle = tier?.title || '';
  const resultImage = tier?.image || '';

  useEffect(() => {
    if (!quiz) {
      navigate('/', { replace: true });
      return;
    }

    const record: ResultRecord = {
      score,
      lifeExpectancy: value,
      title: resultTitle,
      percentile,
      date: new Date().toLocaleDateString('ko-KR'),
    };
    saveResult(record, quiz.id);

    updateMetaTags(
      `${quiz.title} 결과: ${resultTitle}`,
      `${quiz.title} 결과 - ${value}${quiz.resultUnit}, "${resultTitle}" 등급`,
    );
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!quiz) return null;

  const getReactionMessage = (s: number, total: number) => {
    const ratio = (s - (-total * 2)) / (total * 6);
    if (ratio < 0.2) return '지금부터 바꾸면 됩니다! 아직 늦지 않았어요.';
    if (ratio < 0.4) return '개선할 포인트가 보여요. 하나씩 도전해봐요!';
    if (ratio < 0.6) return '나쁘지 않은데요? 조금만 더 신경 쓰면 급상승!';
    if (ratio < 0.8) return '꽤 잘 관리하고 있네요! 이 조자 유지!';
    return '당신... 혹시 건강 유튜버?';
  };

  const handleSaveImage = async () => {
    if (!resultCardRef.current || saving) return;
    setSaving(true);
    try {
      const blob = await generateResultImage(resultCardRef.current);
      if (blob) downloadImage(blob);
    } finally {
      setSaving(false);
    }
  };

  const handleRetry = () => {
    navigate(`/quiz/${quizId}`);
  };

  const currentRecord: ResultRecord = {
    score,
    lifeExpectancy: value,
    title: resultTitle,
    percentile,
    date: new Date().toLocaleDateString('ko-KR'),
  };

  return (
    <div className="wrapper">
      <div className="container">
        <p>계산완료</p>

        {/* Main result display */}
        <div className="dyong-container">
          <p style={{ fontSize: '24px', marginBottom: '1px' }}>
            {quiz.resultPrefix}{value}{quiz.resultUnit}{quiz.resultSuffix}
          </p>
          {resultImage && (
            <img src={resultImage} alt={resultTitle} style={{ width: '200px', height: '200px' }} />
          )}
          <h2>{tier?.emoji} {resultTitle}</h2>
          <p style={{ fontSize: '15px', color: '#555', marginTop: '8px', fontStyle: 'italic' }}>
            "{getReactionMessage(score, quiz.questions.length)}"
          </p>
        </div>

        {/* Percentile badge */}
        <div style={{
          background: `linear-gradient(135deg, ${quiz.gradient[1]}, ${quiz.gradient[0]})`,
          color: '#fff',
          borderRadius: '25px',
          padding: '10px 24px',
          fontSize: '16px',
          fontWeight: 'bold',
          marginBottom: '16px',
          display: 'inline-block',
        }}>
          상위 {percentile}%
        </div>

        {/* Save result image button */}
        <button
          onClick={handleSaveImage}
          disabled={saving}
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '8px',
            width: '100%',
            maxWidth: '280px',
            padding: '14px 20px',
            margin: '10px 0',
            border: 'none',
            borderRadius: '12px',
            fontSize: '15px',
            fontWeight: 'bold',
            cursor: saving ? 'not-allowed' : 'pointer',
            fontFamily: "'TTLaundryGothicB', sans-serif",
            backgroundColor: '#7c4dff',
            color: '#fff',
            boxShadow: '0 2px 8px rgba(124,77,255,0.3)',
            opacity: saving ? 0.7 : 1,
            transition: 'opacity 0.2s',
            float: 'none',
            minWidth: 'unset',
          }}
        >
          {saving ? '저장 중...' : '📷 결과 이미지 저장하기'}
        </button>

        {/* Share buttons */}
        <ShareButtons
          score={score}
          resultTitle={resultTitle}
          lifeExpectancy={value}
          resultCardRef={resultCardRef}
          quizTitle={quiz.title}
          resultUnit={quiz.resultUnit}
        />

        {/* Challenge button */}
        <ChallengeModal
          lifeExpectancy={value}
          resultTitle={resultTitle}
          quizId={quiz.id}
          resultText={`${value}${quiz.resultUnit}`}
        />

        {/* Ad: after main result */}
        <AdUnit format="inarticle" slot="5698320863" />

        {/* Category score breakdown */}
        {answers.length > 0 && (
          <ScoreBreakdown
            answers={answers}
            questions={quiz.questions}
            categories={quiz.categories}
          />
        )}

        {/* Health tips */}
        {answers.length > 0 && (
          <HealthTipsComponent
            answers={answers}
            questions={quiz.questions}
            tips={quiz.healthTips}
          />
        )}

        {/* Result history */}
        <ResultHistory
          currentRecord={currentRecord}
          quizId={quiz.id}
          unit={quiz.resultUnit}
        />

        {/* Retry button */}
        <button
          onClick={handleRetry}
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '8px',
            width: '100%',
            maxWidth: '280px',
            padding: '16px 20px',
            margin: '20px 0',
            border: '3px solid #4CAF50',
            borderRadius: '16px',
            fontSize: '16px',
            fontWeight: 'bold',
            cursor: 'pointer',
            fontFamily: "'TTLaundryGothicB', sans-serif",
            backgroundColor: '#fff',
            color: '#4CAF50',
            transition: 'all 0.3s',
            boxShadow: '0 4px 12px rgba(76,175,80,0.2)',
            float: 'none',
            minWidth: 'unset',
          }}
        >
          🔄 다시 테스트하기
        </button>

        {/* Quiz recommendations + ad */}
        <QuizRecommendations currentQuizId={quiz.id} />

        {/* References */}
        <div style={{ fontSize: '12px', opacity: '0.5', textAlign: 'left', maxWidth: '500px', margin: '30px 0 10px 0' }}>
          [참고 문헌 및 연구 기관]
          <ul style={{ marginRight: '20px', lineHeight: '1.8' }}>
            <li>하버드 대학교 공중보건대학원 (Harvard T.H. Chan School of Public Health, 미국) — 세계 최대 규모의 역학 연구를 수행하며, 간호사건강연구(Nurses' Health Study)와 보건전문가추적연구(Health Professionals Follow-up Study)를 통해 생활습관과 수명 간의 연관성에 대한 핵심적인 근거를 제시하고 있습니다. 수면, 운동, 식이습관이 기대수명에 미치는 영향에 대한 대규모 코호트 연구를 40년 이상 지속하고 있습니다.</li>
            <li>스탠포드 대학교 의과대학 예방연구센터 (Stanford Prevention Research Center, 미국) — 유전체학 및 정밀 의학(Precision Medicine) 분야에서 세계적으로 선도적인 연구를 수행하며, 개인의 유전적 특성과 환경 요인을 결합한 맞춤형 건강 위험도 평가 모델을 개발하고 있습니다. 스탠포드 라이프스타일 의학 프로그램은 생활습관 개선을 통한 만성질환 예방에 관한 임상 연구를 활발히 진행합니다.</li>
            <li>옥스퍼드 대학교 인구보건학부 (Nuffield Department of Population Health, 영국) — UK Biobank 프로젝트를 중심으로 50만 명 이상의 건강 데이터를 분석하여 생활습관, 유전적 요인, 환경적 요인이 질병 발생과 수명에 미치는 영향을 연구합니다. 특히 음주, 흡연, 신체활동과 심혈관 질환의 관계에 대한 메타 분석 연구로 높은 학술적 평가를 받고 있습니다.</li>
            <li>카롤린스카 연구소 (Karolinska Institutet, 스웨덴) — 노벨 생리의학상 심사를 담당하는 기관으로, 신경퇴행성 질환(알츠하이머, 파킨슨) 및 정신건강 분야에서 세계 최고 수준의 연구를 수행합니다. 스웨덴 국가 건강 레지스트리 데이터를 활용한 대규모 종단 연구를 통해 스트레스, 수면 장애, 우울증이 장기 건강 결과에 미치는 영향을 규명하고 있습니다.</li>
            <li>존스 홉킨스 대학교 블룸버그 공중보건대학원 (Johns Hopkins Bloomberg School of Public Health, 미국) — 공중보건 분야 세계 1위로 평가받으며, 만성질환 역학, 건강 행태 연구, 정신건강 역학 분야에서 독보적인 연구 성과를 보유하고 있습니다. 특히 수면과 심혈관 건강의 관계에 대한 연구가 널리 인용됩니다.</li>
            <li>세계보건기구 (World Health Organization, WHO) — 전 세계 194개 회원국의 건강 데이터를 수집·분석하며, 건강 기대수명(HALE), 질병부담(DALY) 등 국제 표준 건강 지표를 개발·관리합니다. 본 테스트의 기준 수치는 WHO에서 발표한 2023년 세계 건강 통계 보고서의 한국 데이터를 참고하였습니다.</li>
            <li>대한예방의학회 (Korean Society for Preventive Medicine) — 국내 예방의학 및 건강증진 분야의 학술 연구를 선도하며, 한국인의 생활습관 질환 위험요인에 대한 역학 연구와 건강 행태 조사를 수행합니다. 국민건강영양조사 데이터를 기반으로 한 한국인 특성에 맞는 건강 지표 연구에 기여하고 있습니다.</li>
            <li>질병관리청 국립보건연구원 (Korea Disease Control and Prevention Agency, KDCA) — 한국인유전체역학조사사업(KoGES)을 통해 24만 명 이상의 한국인 코호트 데이터를 구축하였으며, 한국인의 유전적 특성과 생활습관이 주요 만성질환에 미치는 영향을 체계적으로 분석하고 있습니다.</li>
          </ul>
          <p style={{ marginTop: '8px', fontSize: '11px' }}>
            ※ 본 테스트는 위 기관들의 공개된 연구 결과와 학술 논문을 참고하여 구성되었으며, 의학적 진단을 대체하지 않습니다. 정확한 건강 평가를 위해서는 전문 의료기관의 진료를 받으시기 바랍니다.
          </p>
        </div>

        {/* Offscreen result card for image capture */}
        <ResultCard
          ref={resultCardRef}
          score={score}
          resultTitle={resultTitle}
          resultImage={resultImage}
          quizTitle={quiz.title}
          gradient={quiz.gradient}
          baseValue={quiz.baseValue}
          resultUnit={quiz.resultUnit}
          percentileMean={quiz.percentile.mean}
          percentileSd={quiz.percentile.sd}
        />
      </div>
    </div>
  );
};

export default ResultPage;
